import { defineStore } from "pinia";

import { useSentry } from "@/composables/useSentry.ts";
import axios, { ACCESS_TOKEN, REFRESH_TOKEN } from "@/utils/axios.ts";
import ability from "@/utils/casl/ability.ts";

interface State {
  user: any
  abilities: any[]
  isAdmin: boolean
}

export const useAuthStore = defineStore("auth", {
  state: (): State => {
    return {
      user: {},
      abilities: [],
      isAdmin: false,
    };
  },
  getters: {
    role({ user }) {
      return user?.role;
    },
    getUser({ user }) {
      return user;
    },
    getAbilities({ abilities }) {
      return abilities;
    },
  },
  actions: {
    updateAbilities(abilities: any[] = []) {
      ability.update(abilities);
      this.abilities = abilities;
      localStorage.setItem("userAbilities", JSON.stringify(abilities));
    },
    setUser(user = {}) {
      this.user = user;
    },
    async getMe() {
      const response = await axios.get("/me");
      this.setUser(response.data);
      this.isAdmin = response.data.role === "admin";
      this.updateAbilities(response.data.ability);
      return response;
    },
    changePassword(payload = {}) {
      return axios.post("/change-password", payload);
    },
    async update(payload = {}) {
      const response = await axios.put("/me", payload);
      this.setUser(response.data);
      return response;
    },
    login(email: string, password: string) {
      return new Promise((resolve, reject) => {
        axios
          .post("/login", {
            email: email,
            password: password,
          })
          .then(({ data }) => {
            const { userData } = data;
            this.setUser(userData);

            localStorage.setItem(ACCESS_TOKEN, data.accessToken);
            localStorage.setItem(REFRESH_TOKEN, data.refreshToken);
            this.updateAbilities(userData.ability);
            resolve(userData);
          })
          .catch(reject);
      });
    },
    logout() {
      return new Promise((resolve, reject) => {
        axios
          .post("/logout")
          .then((res) => {
            localStorage.removeItem(ACCESS_TOKEN);
            localStorage.removeItem(REFRESH_TOKEN);
            this.updateAbilities([{ action: "read", subject: "Auth" }]);
            resolve(res);
          })
          .catch(reject);
      });
    },
    cleanUser() {
      this.setUser({});
      this.isAdmin = false;
      useSentry().setScope({
        user: {},
      });
    },
  },
});
